import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { 
  CheckCircle, 
  Download, 
  Mail, 
  ArrowRight, 
  Calendar,
  CreditCard,
  Package,
  ExternalLink,
  AlertCircle
} from 'lucide-react';
import { getOrderByPaymentIntent, getUserSubscription, logOrderConfirmationView } from '../../services/orderService';
import { format } from 'date-fns';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  
  const [orderData, setOrderData] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      const paymentIntentId = searchParams?.get('payment_intent');
      const orderId = searchParams?.get('order_id');

      if (!paymentIntentId && !orderId) {
        setError('No order information found. Please check your email for order details.');
        setLoading(false);
        return;
      }

      try {
        let orderResult;
        
        if (paymentIntentId) {
          orderResult = await getOrderByPaymentIntent(paymentIntentId);
        }

        if (orderResult?.order) {
          setOrderData(orderResult?.order);
          
          // Fetch subscription if user is logged in
          if (user?.id || orderResult?.order?.user_id) {
            const userId = user?.id || orderResult?.order?.user_id;
            const { subscription: subData } = await getUserSubscription(userId);
            setSubscription(subData);
            
            // Log activity
            if (userId && orderResult?.order?.order_number) {
              await logOrderConfirmationView(userId, orderResult?.order?.order_number);
            }
          }
        } else {
          setError(orderResult?.error?.message || 'Failed to load order details');
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('An error occurred while loading your order details');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [searchParams, user]);

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency?.toUpperCase() || 'USD'
    })?.format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM dd, yyyy h:mm a');
    } catch (err) {
      return 'N/A';
    }
  };

  const handleDownloadReceipt = () => {
    // Implement PDF receipt download
    window.print();
  };

  const getPlanFeatures = (planId) => {
    const features = {
      free: ['Basic features', 'Community support', '1 project'],
      pro: ['Unlimited projects', 'Priority support', 'Advanced features', 'Custom domains'],
      business: ['Everything in Pro', 'Team collaboration', 'Advanced analytics', 'SLA guarantee'],
      enterprise: ['Everything in Business', 'Dedicated support', 'Custom integrations', 'White-label options']
    };
    return features?.[planId?.toLowerCase()] || features?.free;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your order details...</p>
        </div>
      </div>
    );
  }

  if (error || !orderData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center border border-red-200">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/subscription-management')}
            className="w-full bg-indigo-600 text-white py-3 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Go to Subscriptions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Success Header */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-6 border border-green-100">
          <div className="flex items-center justify-center mb-6">
            <div className="bg-green-100 rounded-full p-4">
              <CheckCircle className="w-16 h-16 text-green-600" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 text-center mb-2">
            Order Confirmed!
          </h1>
          <p className="text-gray-600 text-center mb-6">
            Thank you for your purchase. Your order has been successfully processed.
          </p>
          
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <div className="flex items-center">
              <Package className="w-4 h-4 mr-2" />
              <span>Order #{orderData?.order_number}</span>
            </div>
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-2" />
              <span>{formatDate(orderData?.created_at)}</span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Payment Receipt */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Payment Receipt</h2>
                <button
                  onClick={handleDownloadReceipt}
                  className="flex items-center text-indigo-600 hover:text-indigo-700 transition-colors"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Download PDF
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                  <div>
                    <p className="text-sm text-gray-500">Plan</p>
                    <p className="font-semibold text-gray-900">{orderData?.project_name || 'Subscription'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-gray-900">
                      {formatCurrency(orderData?.subtotal, orderData?.currency)}
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(orderData?.subtotal, orderData?.currency)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax</span>
                    <span>{formatCurrency(orderData?.tax_amount, orderData?.currency)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                    <span className="font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-indigo-600">
                      {formatCurrency(orderData?.total_amount, orderData?.currency)}
                    </span>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 mt-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <CreditCard className="w-4 h-4 mr-2" />
                    <span>
                      Payment Status: 
                      <span className={`ml-2 font-semibold ${
                        orderData?.payment_status === 'succeeded' ? 'text-green-600' : 
                        orderData?.payment_status === 'pending'? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {orderData?.payment_status?.charAt(0)?.toUpperCase() + orderData?.payment_status?.slice(1)}
                      </span>
                    </span>
                  </div>
                  {orderData?.payment_transactions?.[0]?.stripe_charge_id && (
                    <p className="text-xs text-gray-500 mt-2">
                      Transaction ID: {orderData?.payment_transactions?.[0]?.stripe_charge_id}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Subscription Details */}
            {subscription && (
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Subscription Details</h2>
                
                <div className="space-y-4">
                  <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-100">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-gray-900">Plan Features</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        subscription?.status === 'active' ? 'bg-green-100 text-green-700' :
                        subscription?.status === 'trialing'? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {subscription?.status?.charAt(0)?.toUpperCase() + subscription?.status?.slice(1)}
                      </span>
                    </div>
                    <ul className="space-y-2 mt-3">
                      {getPlanFeatures(subscription?.plan_id)?.map((feature, index) => (
                        <li key={index} className="flex items-center text-gray-700">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 mb-1">Activation Date</p>
                      <p className="font-semibold text-gray-900">
                        {formatDate(subscription?.current_period_start)}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <p className="text-sm text-gray-500 mb-1">Next Billing Date</p>
                      <p className="font-semibold text-gray-900">
                        {formatDate(subscription?.current_period_end)}
                      </p>
                    </div>
                  </div>

                  {subscription?.cancel_at_period_end && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-800">
                        ⚠️ Your subscription will cancel at the end of the current billing period
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Next Steps */}
            <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Next Steps</h2>
              
              <div className="space-y-4">
                <button
                  onClick={() => navigate('/project-dashboard')}
                  className="w-full flex items-center justify-between bg-indigo-600 text-white py-3 px-4 rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <span>Go to Dashboard</span>
                  <ArrowRight className="w-5 h-5" />
                </button>

                <button
                  onClick={() => navigate('/subscription-management')}
                  className="w-full flex items-center justify-between bg-gray-100 text-gray-900 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <span>Manage Subscription</span>
                  <ArrowRight className="w-5 h-5" />
                </button>

                <button
                  onClick={() => navigate('/project-settings')}
                  className="w-full flex items-center justify-between bg-gray-100 text-gray-900 py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <span>Project Settings</span>
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Email Confirmation */}
            <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-100">
              <div className="flex items-start">
                <Mail className="w-6 h-6 text-indigo-600 mr-3 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Email Confirmation Sent</h3>
                  <p className="text-sm text-gray-600">
                    A detailed receipt and order confirmation has been sent to{' '}
                    <span className="font-medium text-gray-900">
                      {orderData?.user_profiles?.email || 'your email'}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Support */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Our support team is here to help you get started.
              </p>
              <a
                href="mailto:support@example.com"
                className="flex items-center text-indigo-600 hover:text-indigo-700 text-sm font-medium"
              >
                Contact Support
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;